/**
 * FTP Settings Service - API client for FTP configuration endpoints
 * Handles all HTTP communication with the FTP settings backend
 */

import { createScopedReporter } from './errorReporter';
import { cognitoService } from './cognitoService';
import { API_BASE_URL, API_KEY } from '@/config/api';

const SERVICE_TAG = '[FTP_SETTINGS_API]';
const reporter = createScopedReporter('FTP');

/** Storage mode options */
export type StorageMode = 'cloud' | 'ftp' | 'both';

/** FTP configuration input */
export interface FtpConfigInput {
  host: string;
  port: number;
  username: string;
  password: string;
  basePath: string;
  useFtps: true;
}

/** FTP status response */
export interface FtpStatus {
  configured: boolean;
  storageMode: StorageMode;
  host?: string;
  port?: number;
  basePath?: string;
  lastTestedAt?: string;
  connectionStatus?: 'success' | 'failed' | 'unknown';
}

/** Connection test result */
export interface FtpConnectionTestResult {
  success: boolean;
  message: string;
  serverInfo?: string;
  latencyMs?: number;
}

/** FTP upload request */
export interface FtpUploadRequest {
  deviceId: string;
  diceType: string;
  faceNumber: number;
  imageBase64: string;
  rotationIndex?: number;
}

/** FTP upload response */
export interface FtpUploadResponse {
  remotePath: string;
  bytesUploaded: number;
}

/**
 * Make authenticated request to the API
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const idToken = await cognitoService.getIdToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Api-Key': API_KEY,
  };
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...(options.headers as Record<string, string>)
    }
  });

  const data = await response.json().catch(() => ({ message: 'Unknown error' }));

  if (!response.ok) {
    reporter.error('Remote Archive API error', {
      technicalDetails: `${endpoint} returned ${response.status}`,
      context: { endpoint, status: response.status },
      showToast: false
    });
    throw new Error(data.error?.message || data.message || `API error: ${response.status}`);
  }

  return data.data || data;
}

/**
 * Convert Blob to base64 string
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      // Remove data URL prefix (data:image/jpeg;base64,)
      const base64Data = base64.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * FTP Settings API Service
 */
export const ftpSettingsService = {
  /**
   * Save FTP configuration
   * Validates connection before saving
   *
   * @param deviceId - Device identifier
   * @param config - FTP server configuration
   * @param storageMode - Storage mode selection
   */
  async saveFtpConfig(
    deviceId: string,
    config: FtpConfigInput,
    storageMode: StorageMode
  ): Promise<void> {
    console.info(`${SERVICE_TAG} Saving FTP config`, {
      deviceId,
      storageMode,
      host: config.host,
      port: config.port
    });

    await apiRequest('/settings/ftp', {
      method: 'POST',
      body: JSON.stringify({ deviceId, config, storageMode })
    });
  },

  /**
   * Test FTP connection without saving
   *
   * @param deviceId - Device identifier
   * @param config - FTP server configuration
   * @returns Connection test result
   */
  async testConnection(
    deviceId: string,
    config: FtpConfigInput
  ): Promise<FtpConnectionTestResult> {
    console.info(`${SERVICE_TAG} Testing FTP connection`, {
      deviceId,
      host: config.host,
      port: config.port
    });

    return apiRequest<FtpConnectionTestResult>('/settings/ftp/test', {
      method: 'POST',
      body: JSON.stringify({ deviceId, config })
    });
  },

  /**
   * Get FTP configuration status (no credentials returned)
   *
   * @param deviceId - Device identifier
   * @returns FTP status
   */
  async getFtpStatus(deviceId: string): Promise<FtpStatus> {
    console.info(`${SERVICE_TAG} Getting FTP status`, { deviceId });

    return apiRequest<FtpStatus>(`/settings/ftp/${deviceId}`);
  },

  /**
   * Delete FTP configuration
   *
   * @param deviceId - Device identifier
   */
  async deleteFtpConfig(deviceId: string): Promise<void> {
    console.info(`${SERVICE_TAG} Deleting FTP config`, { deviceId });

    await apiRequest(`/settings/ftp/${deviceId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Upload image to FTP server via backend proxy
   *
   * @param deviceId - Device identifier
   * @param diceType - Dice type
   * @param faceNumber - Face number (1-based)
   * @param imageBlob - Image data as Blob
   * @param rotationIndex - Optional rotation variant index
   * @returns Upload result with remote path
   */
  async uploadToFtp(
    deviceId: string,
    diceType: string,
    faceNumber: number,
    imageBlob: Blob,
    rotationIndex?: number
  ): Promise<FtpUploadResponse> {
    console.info(`${SERVICE_TAG} Uploading to FTP`, {
      deviceId,
      diceType,
      faceNumber,
      rotationIndex,
      blobSize: imageBlob.size
    });

    const imageBase64 = await blobToBase64(imageBlob);

    return apiRequest<FtpUploadResponse>('/dice-registry/upload-ftp', {
      method: 'POST',
      body: JSON.stringify({
        deviceId,
        diceType,
        faceNumber,
        imageBase64,
        rotationIndex
      })
    });
  }
};
